// The helper tests execute TypeScript directly with Node's strip-types loader.
// Keep the source extension here while suppressing tsc's emitted-module warning.
// @ts-expect-error TypeScript source import is intentional for the direct Node test.
import { catchAllMailboxIds, parseMailboxAddresses, primaryMailboxAddress } from './mailbox-addresses.ts';
import type { MailAdminMailbox, MailRouteDraft } from './api';

export type MailboxAlternativeRow = {
  id: string;
  value: string;
};

export type MailboxFormValue = {
  displayName: string;
  primaryAddress: string;
  description: string;
  alternatives: MailboxAlternativeRow[];
  catchAll: boolean;
};

export type MailboxProfileInput = {
  displayName: string;
  primaryAddress: string;
  description: string;
  aliases: string[];
  catchAll: boolean;
};

export function domainRoute(draft: MailRouteDraft, domain: string): MailRouteDraft['domains'][number] | null {
  const normalizedDomain = normalizeDomain(domain);
  return draft.domains.find((route) => normalizeDomain(route.domain) === normalizedDomain) ?? null;
}

export function mailboxesForDomain(draft: MailRouteDraft, domain: string): MailAdminMailbox[] {
  const route = domainRoute(draft, domain);
  const normalizedDomain = normalizeDomain(domain);
  const referencedIds = new Set<string>([
    ...(route?.addresses ?? []).map((entry) => entry.mailbox),
    ...(route?.aliases ?? []).map((entry) => entry.mailbox),
    ...catchAllMailboxIds(route ?? {})
  ]);

  return draft.mailboxes.filter((mailbox) => {
    const primary = primaryMailboxAddress(mailbox.primaryEmail);
    const primaryDomain = primary?.split('@')[1] ?? '';
    return referencedIds.has(mailbox.id) || primaryDomain === normalizedDomain;
  });
}

export function mailboxDisplayName(mailbox: MailAdminMailbox, draft: MailRouteDraft): string {
  const explicitName = mailbox.displayName?.trim();
  if (explicitName) return explicitName;

  const owner = draft.users.find((user) => user.id === mailbox.owner || user.mailboxAccess.includes(mailbox.id));
  return owner?.displayName?.trim() || 'Mailbox';
}

export function mailboxOptionLabel(mailbox: MailAdminMailbox, draft: MailRouteDraft, index = 0): string {
  const name = mailboxDisplayName(mailbox, draft);
  const primary = primaryMailboxAddress(mailbox.primaryEmail);
  if (primary && name !== primary) return `${name} · ${primary}`;
  return name === 'Mailbox' && !primary ? `Mailbox ${index + 1}` : name;
}

export function mailboxFormValue(draft: MailRouteDraft, domain: string, mailboxId: string | null): MailboxFormValue {
  if (!mailboxId) {
    return {
      displayName: '',
      primaryAddress: '',
      description: '',
      alternatives: [{ id: 'alternative-0', value: '' }],
      catchAll: false
    };
  }

  const mailbox = draft.mailboxes.find((candidate) => candidate.id === mailboxId);
  if (!mailbox) {
    return {
      displayName: '',
      primaryAddress: '',
      description: '',
      alternatives: [{ id: 'alternative-0', value: '' }],
      catchAll: false
    };
  }

  const route = domainRoute(draft, domain);
  const primary = primaryMailboxAddress(mailbox.primaryEmail);
  const alternatives = uniqueAddresses([
    ...(route?.addresses ?? [])
      .filter((entry) => entry.mailbox === mailboxId)
      .map((entry) => entry.address),
    ...(route?.aliases ?? [])
      .filter((entry) => entry.mailbox === mailboxId)
      .map((entry) => entry.address)
  ].filter((address) => {
    const normalized = normalizeAddress(address);
    return normalized !== primary && addressDomain(normalized) === normalizeDomain(domain);
  }));

  return {
    displayName: mailboxDisplayName(mailbox, draft),
    primaryAddress: primary ?? '',
    description: mailbox.description?.trim() || '',
    alternatives: (alternatives.length > 0 ? alternatives : ['']).map((value, index) => ({
      id: `alternative-${index}`,
      value
    })),
    catchAll: catchAllMailboxIds(route ?? {}).includes(mailboxId)
  };
}

export function buildMailboxProfileInput(value: MailboxFormValue, domain: string): MailboxProfileInput {
  const displayName = value.displayName.trim();
  if (!displayName) throw new Error('Enter a name for this mailbox.');

  const normalizedDomain = normalizeDomain(domain);
  const primaryAddresses = parseMailboxAddresses(value.primaryAddress, normalizedDomain, true);
  if (primaryAddresses.length !== 1) throw new Error('Enter one primary email address.');
  const primaryAddress = primaryAddresses[0];
  const aliases = parseMailboxAddresses(
    value.alternatives.map((row) => row.value).join('\n'),
    normalizedDomain,
    false
  );
  if (aliases.includes(primaryAddress)) {
    throw new Error('An address cannot also be the primary login and an alternative.');
  }

  return {
    displayName,
    primaryAddress,
    description: value.description.trim(),
    aliases,
    catchAll: value.catchAll
  };
}

export function draftIsStale(baseRevision: number, currentRevision: number): boolean {
  return baseRevision !== currentRevision;
}

export function canSwitchMailbox(dirty: boolean, busy: boolean): boolean {
  return !dirty && !busy;
}

function normalizeDomain(domain: string): string {
  return domain.trim().toLowerCase();
}

function normalizeAddress(address: string): string {
  return address.trim().toLowerCase();
}

function addressDomain(address: string): string {
  return address.slice(address.lastIndexOf('@') + 1);
}

function uniqueAddresses(addresses: string[]): string[] {
  const seen = new Set<string>();
  return addresses.reduce<string[]>((result, address) => {
    const normalized = normalizeAddress(address);
    if (!normalized || seen.has(normalized)) return result;
    seen.add(normalized);
    result.push(normalized);
    return result;
  }, []);
}
